import type { InviteAccessLevel } from "@/auth/permissions";
import { supabase } from "@/lib/supabase";

export interface InviteCodeRow {
  id: string;
  code: string;
  max_uses: number;
  use_count: number;
  note: string | null;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
  active: boolean;
  access_level: InviteAccessLevel;
}

export interface CreatedInviteCode {
  id: string;
  code: string;
  max_uses: number;
  expires_at: string | null;
  access_level: InviteAccessLevel;
}

export async function fetchIsWarehouseAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("wh_is_warehouse_admin");
  if (error) throw error;
  return Boolean(data);
}

export async function createInviteCode(params: {
  maxUses: number;
  note?: string;
  expiresInDays?: number;
  accessLevel?: InviteAccessLevel;
}): Promise<CreatedInviteCode> {
  const { data, error } = await supabase.rpc("wh_create_invite_code", {
    p_max_uses: params.maxUses,
    p_note: params.note?.trim() || null,
    p_expires_in_days: params.expiresInDays ?? null,
    p_access_level: params.accessLevel ?? "normal",
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.code) {
    throw new Error("Invite code creation returned no code.");
  }

  return {
    id: row.id as string,
    code: row.code as string,
    max_uses: row.max_uses as number,
    expires_at: (row.expires_at as string | null) ?? null,
    access_level: (row.access_level as InviteAccessLevel) ?? "normal",
  };
}

export async function listInviteCodes(): Promise<InviteCodeRow[]> {
  const { data, error } = await supabase.rpc("wh_list_invite_codes");
  if (error) throw error;
  return (data ?? []) as InviteCodeRow[];
}

export async function revokeInviteCode(id: string): Promise<void> {
  const { error } = await supabase.rpc("wh_revoke_invite_code", { p_id: id });
  if (error) throw error;
}
